import type { useTranslations } from 'next-intl'

import { USD_PER_CREDIT } from '@/lib/billing/credit-economics'
import { BILLING_PLANS } from '@/lib/billing/plans'
import {
  defaultPriceSummary,
  formatCurrency,
} from '@/lib/billing/price-summary'
import type { Locale } from '@/lib/i18n/config'
import { formatCompactNumber, numberFormat } from '@/lib/i18n/format'
import type { Messages } from '@/lib/i18n/messages'
import { BREW_DEMO_BOOKING_URL } from '@/lib/site/booking'
import { LEGAL_LINKS } from '@/lib/site/legal-links'

/**
 * The /enterprise page: one Enterprise plan the reader builds. The builder,
 * the page's FAQ (`enterpriseFaqValues`), its Markdown twin
 * (`/enterprise.md`) and its OG card all read these numbers, so they live in
 * `lib/` where the SEO modules may import them.
 * Their words are the `enterpriseContent` catalog: every helper that writes
 * text takes its translator and the reader's locale (`EnterpriseCopy`), so
 * the three say the same thing in every language.
 *
 * A platform fee covers dedicated support, team controls, unlimited brands
 * and data privacy; the reader picks AI credits and
 * emails from tiers that start above Pro and cost less per unit the more
 * they take, then the optional add-ons: migration support and managed
 * lifecycle. Every line says what it
 * costs, and the total updates as they choose.
 *
 * The prices are the founders' working numbers; change them here and the
 * builder and its total follow.
 */

/** `useTranslations('enterpriseContent')`, or `getTranslations` for it. */
export type EnterpriseContentTranslator = ReturnType<
  typeof useTranslations<'enterpriseContent'>
>

/** The plan's words and the locale its numbers and prices format in. */
export type EnterpriseCopy = Readonly<{
  t: EnterpriseContentTranslator
  locale: Locale
}>

type ContentMessages = Messages['enterpriseContent']

export type VolumeOption = Readonly<{
  /** Units a month; `null` for "more than this", priced on a call. */
  amount: number | null
  /** Dollars a month for this tier; `null` when priced on a call. */
  addUsd: number | null
}>

/** An add-on: its words are `enterpriseContent.addOns.<id>`. */
export type EnterpriseAddOn = Readonly<{
  id: keyof ContentMessages['addOns']
  priceUsd: number
}>

/** An add-on with its name and what it is, in the reader's language. */
export type AddOnLine = EnterpriseAddOn &
  Readonly<{ name: string; description: string }>

/**
 * The platform fee: what every Enterprise plan pays for dedicated support,
 * team controls, unlimited brands and data privacy, before any credits or
 * emails. Some of it is on self-serve plans too; it is listed because
 * procurement checks for it. Each line is something we have today; its
 * words are `enterpriseContent.base.included.<id>`.
 * `credits` and `sends` are the smallest tiers a plan can pick.
 */
export type IncludedItem = Readonly<{
  id: keyof ContentMessages['base']['included']
  /** Documents a reviewer can open, shown under the line. */
  links?: ReadonlyArray<Readonly<{ label: string; href: string }>>
}>

/** An included line with its title and description, in the reader's language. */
export type IncludedLine = IncludedItem &
  Readonly<{ title: string; description: string }>

export const ENTERPRISE_BASE: Readonly<{
  priceUsd: number
  credits: number
  sends: number
  included: ReadonlyArray<IncludedItem>
}> = {
  priceUsd: 499,
  credits: 25_000,
  sends: 250_000,
  included: [
    { id: 'dedicatedSupport' },
    { id: 'team' },
    { id: 'brands' },
    { id: 'privacy', links: LEGAL_LINKS },
  ],
}

/** Dedicated support answers in the shared Slack channel within this many hours. */
export const SUPPORT_RESPONSE_HOURS = 48

/** A managed-lifecycle email asked for on a weekday is ready within this many hours. */
export const LIFECYCLE_TURNAROUND_HOURS = 48

/** The figures the plan's descriptions name (`base.included`, `addOns`). */
const DESCRIPTION_VALUES = {
  supportHours: SUPPORT_RESPONSE_HOURS,
  turnaroundHours: LIFECYCLE_TURNAROUND_HOURS,
} as const

/** What the platform fee includes, in page order. */
export function buildIncludedLines(
  t: EnterpriseContentTranslator
): ReadonlyArray<IncludedLine> {
  return ENTERPRISE_BASE.included.map((item) => ({
    ...item,
    title: t(`base.included.${item.id}.title`),
    description: t(`base.included.${item.id}.description`, DESCRIPTION_VALUES),
  }))
}

/** AI credits a month. Every plan picks one; the first is the smallest. */
export const CREDIT_OPTIONS: ReadonlyArray<VolumeOption> = [
  { amount: 25_000, addUsd: 230 },
  { amount: 50_000, addUsd: 455 },
  { amount: 100_000, addUsd: 875 },
]

/** Emails a month. Every plan picks one; the first is the smallest. */
export const SEND_OPTIONS: ReadonlyArray<VolumeOption> = [
  { amount: 250_000, addUsd: 270 },
  { amount: 500_000, addUsd: 530 },
  { amount: 1_000_000, addUsd: 1025 },
  { amount: 2_500_000, addUsd: 2425 },
  { amount: 5_000_000, addUsd: 4730 },
  { amount: 10_000_000, addUsd: 9300 },
  { amount: null, addUsd: null },
]

/**
 * What a unit costs us. A credit is $0.01 of model spend by definition
 * (`USD_PER_CREDIT`). An email costs $0.20 per 1,000 on Mailgun, where
 * sending is moving (Sinch service order #00005098, Aug 2026 to Aug 2027),
 * and about $0.65 per 1,000 on Resend's transactional list rate, the
 * dearer of our two senders, with no volume discount.
 */
export const UNIT_COST_USD = {
  credit: USD_PER_CREDIT,
  email: { mailgun: 0.2 / 1000, resend: 0.65 / 1000 },
} as const

/**
 * Credits a new email uses, to say what a credit amount gets you. An email
 * costs what the model actually used (`lib/billing/operation-credits.ts`
 * meters `email.generate` by usage), so this is the founders' planning rate:
 * about 100 credits a new email, with real ones between ~33 and ~167.
 * Edits are metered too, so a long email can cost more; audits (5), previews
 * (10) and images use few. Importing HTML, MJML, React Email or Figma and
 * exporting are free; turning a screenshot into an email is not.
 */
export const CREDITS_PER_NEW_EMAIL = 100

/** Roughly how many new emails a credit amount designs: 250 for 25,000. */
export function emailsDesigned(credits: number): number {
  return Math.round(credits / CREDITS_PER_NEW_EMAIL)
}

/**
 * "Enough to design about 250 new emails a month.": the page's line under
 * the credit tiers, as plain text (the page sets the amount in bold).
 */
export function creditsInEmails(
  credits: number,
  t: EnterpriseContentTranslator
): string {
  return t.markup('credits.designs', {
    count: emailsDesigned(credits),
    strong: (chunks) => chunks,
  })
}

/** The least gross margin a line may earn at full use, at list cost. */
export const MIN_GROSS_MARGIN = 0.3

/** Dollars from a price summary label ("$249" → 249). */
function labelUsd(label: string | undefined, fallback: number): number {
  const value = Number(label?.replace(/[^0-9.]/g, ''))
  return Number.isFinite(value) && value > 0 ? value : fallback
}

/**
 * What one credit and one email cost on Pro, the largest self-serve plan:
 * its monthly price over what it includes. The badges compare each
 * Enterprise step against buying the same amount at this rate.
 */
export function selfServeUnitRate(): Readonly<{
  credit: number
  email: number
}> {
  const pro = labelUsd(defaultPriceSummary().pro?.label, 249)
  return {
    credit: pro / BILLING_PLANS.pro.credits,
    email: pro / BILLING_PLANS.pro.emailSends,
  }
}

/** The decimals of a dollar the overage price keeps: a thousandth of a cent. */
const OVERAGE_DECIMALS = 5

/**
 * What each email over the chosen tier costs, in dollars: Pro's rate, so a
 * tier is always cheaper than going over it. Enterprise orgs are not gated at
 * their tier, and nothing meters them per month yet (the `send_quota_overage`
 * alert skips plans with no included sends), so the founders count the extra
 * from `billingUsageEvents` and invoice it with the next month.
 */
export function emailOverageUsd(): number {
  const scale = 10 ** OVERAGE_DECIMALS
  return Math.round(selfServeUnitRate().email * scale) / scale
}

/** The overage price in `locale`: "$0.00125" an email, "$1.25" per "1,000". */
function emailOverageValues(locale: Locale) {
  const perEmail = emailOverageUsd()
  return {
    perEmail: numberFormat(locale, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: OVERAGE_DECIMALS,
    }).format(perEmail),
    perThousand: formatUsd(Math.round(perEmail * 1000 * 100) / 100, locale),
    thousand: numberFormat(locale).format(1000),
  }
}

/**
 * "Emails over your tier keep sending, at $0.00125 each ($1.25 per 1,000),
 * added to your next invoice."
 */
export function formatEmailOverage({ t, locale }: EnterpriseCopy): string {
  return t('overage', emailOverageValues(locale))
}

/**
 * What a tier saves against the same volume at Pro's rate, as a whole
 * percent ("Save 12%"). `null` for the open-ended tier.
 */
export function volumeSavingsPercent(
  options: ReadonlyArray<VolumeOption>,
  index: number,
  selfServeRate: number
): number | null {
  const option = options[index]
  if (!option || option.amount === null || option.addUsd === null) {
    return null
  }
  const percent = Math.round(
    (1 - option.addUsd / (selfServeRate * option.amount)) * 100
  )
  return percent > 0 ? percent : null
}

/** "12%": a saving from `volumeSavingsPercent`, in `locale`. */
export function formatSavingsPercent(percent: number, locale: Locale): string {
  return numberFormat(locale, { style: 'percent' }).format(percent / 100)
}

export const ENTERPRISE_ADD_ONS: ReadonlyArray<EnterpriseAddOn> = [
  { id: 'migration', priceUsd: 999 },
  { id: 'managedLifecycle', priceUsd: 2999 },
]

/** The add-ons, in page order. */
export function buildAddOnLines(
  t: EnterpriseContentTranslator
): ReadonlyArray<AddOnLine> {
  return ENTERPRISE_ADD_ONS.map((addOn) => ({
    ...addOn,
    name: t(`addOns.${addOn.id}.name`),
    description: t(`addOns.${addOn.id}.description`, DESCRIPTION_VALUES),
  }))
}

export type EnterpriseSelection = Readonly<{
  creditIndex: number
  sendIndex: number
  addOns: ReadonlyArray<EnterpriseAddOn['id']>
}>

export type EnterpriseQuote = Readonly<{
  /** `null` when a choice is priced on a call. */
  monthlyUsd: number | null
  lines: ReadonlyArray<Readonly<{ label: string; usd: number | null }>>
}>

/** "$1,025" in `locale`. */
export function formatUsd(amount: number, locale: Locale): string {
  return formatCurrency({ amount, currency: 'USD', locale })
}

/**
 * An option's short label: "250K", "1M" (`formatCompactNumber`); the
 * open-ended last option reads "10M+".
 */
export function volumeLabel(
  options: ReadonlyArray<VolumeOption>,
  index: number,
  { t, locale }: EnterpriseCopy
): string {
  const option = options[index]
  if (!option) {
    return ''
  }
  if (option.amount === null) {
    return t('tiers.more', {
      volume: formatCompactNumber(locale, options.at(-2)?.amount ?? 0),
    })
  }
  return formatCompactNumber(locale, option.amount)
}

/** The email and credit tiers picked, the add-ons, then the platform fee. */
export function getEnterpriseQuote(
  selection: EnterpriseSelection,
  copy: EnterpriseCopy
): EnterpriseQuote {
  const { t } = copy
  const credit = CREDIT_OPTIONS[selection.creditIndex]
  const send = SEND_OPTIONS[selection.sendIndex]
  const lines: Array<{ label: string; usd: number | null }> = []
  if (send) {
    lines.push({
      label: t('quote.emails', {
        volume: volumeLabel(SEND_OPTIONS, selection.sendIndex, copy),
      }),
      usd: send.addUsd,
    })
  }
  if (credit) {
    lines.push({
      label: t('quote.credits', {
        volume: volumeLabel(CREDIT_OPTIONS, selection.creditIndex, copy),
      }),
      usd: credit.addUsd,
    })
  }
  for (const addOn of ENTERPRISE_ADD_ONS) {
    if (selection.addOns.includes(addOn.id)) {
      lines.push({ label: t(`addOns.${addOn.id}.name`), usd: addOn.priceUsd })
    }
  }
  // Last, as on the page: the choices first, then the fee every plan pays.
  lines.push({ label: t('base.name'), usd: ENTERPRISE_BASE.priceUsd })
  const monthlyUsd = lines.some((line) => line.usd === null)
    ? null
    : lines.reduce((sum, line) => sum + (line.usd ?? 0), 0)
  return { monthlyUsd, lines }
}

/** The least an Enterprise plan costs: the fee and the smallest tiers. */
export const ENTERPRISE_STARTING_USD =
  ENTERPRISE_BASE.priceUsd +
  (CREDIT_OPTIONS[0]?.addUsd ?? 0) +
  (SEND_OPTIONS[0]?.addUsd ?? 0)

/**
 * A tier's amount in running text, in `locale`: "250,000", and millions in
 * words, "2.5 million".
 */
function spokenAmount(amount: number, locale: Locale): string {
  return amount >= 1_000_000
    ? numberFormat(locale, {
        notation: 'compact',
        compactDisplay: 'long',
        maximumFractionDigits: 1,
      }).format(amount)
    : numberFormat(locale).format(amount)
}

/**
 * Each priced tier's amount and price, numbered from 1 in page order:
 * `emails1` and `emailsPrice1` for the first email tier.
 */
function tierValues(
  prefix: 'emails' | 'credits',
  options: ReadonlyArray<VolumeOption>,
  locale: Locale
): Record<string, string> {
  const values: Record<string, string> = {}
  for (const [index, option] of options.entries()) {
    if (option.amount !== null && option.addUsd !== null) {
      values[`${prefix}${index + 1}`] = spokenAmount(option.amount, locale)
      values[`${prefix}Price${index + 1}`] = formatUsd(option.addUsd, locale)
    }
  }
  return values
}

/** What an add-on costs a month, in `locale`. */
function addOnPrice(id: EnterpriseAddOn['id'], locale: Locale): string {
  const addOn = ENTERPRISE_ADD_ONS.find((candidate) => candidate.id === id)
  return formatUsd(addOn?.priceUsd ?? 0, locale)
}

/**
 * The figures the Enterprise FAQ (`pricingContent.faq.enterprise`) names,
 * from the numbers the builder prices with: prices and amounts formatted in
 * `locale`, counts as numbers for the messages' plurals. The page, its
 * FAQPage JSON-LD and the Markdown twin all read the FAQ through
 * `buildFaqItems`, so they quote the same plan.
 */
export function enterpriseFaqValues(
  locale: Locale
): Record<string, string | number> {
  const rate = selfServeUnitRate().email
  const savings = SEND_OPTIONS.flatMap(
    (_, index) => volumeSavingsPercent(SEND_OPTIONS, index, rate) ?? []
  )
  const sends = SEND_OPTIONS.flatMap((option) => option.amount ?? [])
  return {
    platformFee: formatUsd(ENTERPRISE_BASE.priceUsd, locale),
    startingPrice: formatUsd(ENTERPRISE_STARTING_USD, locale),
    migrationPrice: addOnPrice('migration', locale),
    lifecyclePrice: addOnPrice('managedLifecycle', locale),
    ...tierValues('emails', SEND_OPTIONS, locale),
    ...tierValues('credits', CREDIT_OPTIONS, locale),
    fewestEmails: spokenAmount(sends[0] ?? 0, locale),
    mostEmails: spokenAmount(sends.at(-1) ?? 0, locale),
    smallestSaving: formatSavingsPercent(Math.min(...savings), locale),
    largestSaving: formatSavingsPercent(Math.max(...savings), locale),
    baseCredits: ENTERPRISE_BASE.credits,
    designedEmails: emailsDesigned(ENTERPRISE_BASE.credits),
    creditsPerEmail: CREDITS_PER_NEW_EMAIL,
    ...emailOverageValues(locale),
    proEmails: BILLING_PLANS.pro.emailSends,
    supportHours: SUPPORT_RESPONSE_HOURS,
    turnaroundHours: LIFECYCLE_TURNAROUND_HOURS,
  }
}

export const ENTERPRISE_CTA_URL = BREW_DEMO_BOOKING_URL
